import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type { PaperFilters, SortField, SortDirection } from "@/hooks/useFilteredPapers";

export interface FilterPreset {
  id: string;
  name: string;
  filters: PaperFilters;
  sort_field?: SortField | null;
  sort_direction?: SortDirection | null;
  is_default: boolean;
  created_at: string;
}

interface CreatePresetInput {
  name: string;
  filters: PaperFilters;
  sort_field?: SortField;
  sort_direction?: SortDirection;
  is_default?: boolean;
}

export function useFilterPresets(conceptId: string) {
  return useQuery({
    queryKey: ["filter-presets", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/filter-presets`)
        .json<FilterPreset[]>(),
    enabled: !!conceptId,
  });
}

export function useFilterPresetActions(conceptId: string) {
  const queryClient = useQueryClient();
  const key = ["filter-presets", conceptId];

  const createPreset = useMutation({
    mutationFn: (data: CreatePresetInput) =>
      apiClient
        .post(`api/concepts/${conceptId}/filter-presets`, { json: data })
        .json<FilterPreset>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const updatePreset = useMutation({
    mutationFn: ({
      presetId,
      ...data
    }: {
      presetId: string;
      name?: string;
      filters?: PaperFilters;
      sort_field?: SortField;
      sort_direction?: SortDirection;
      is_default?: boolean;
    }) =>
      apiClient
        .patch(`api/concepts/${conceptId}/filter-presets/${presetId}`, {
          json: data,
        })
        .json<FilterPreset>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const deletePreset = useMutation({
    mutationFn: (presetId: string) =>
      apiClient.delete(`api/concepts/${conceptId}/filter-presets/${presetId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  return { createPreset, updatePreset, deletePreset };
}
